import { useCallback, useEffect, useMemo } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormInput } from '@microservices/wiskey-react-components'
import { Delete } from '@mui/icons-material'
import { Box, Divider, IconButton, Typography } from '@mui/material'

import { GENERATOR_INPUT_TYPE } from '@constants'
import { ObjectFieldDTO, SelectOption } from '@types'

import { ConfigField, SORT_ORDER_TYPE } from '@gantt/components/GanttCreateOrEdit'
import { ModalRefPathPicker } from '@gantt/components/GanttCreateOrEdit/components/ModalRefPathPicker'
import { getBindTypeOptionsByEnum } from '@gantt/helpers'

import { SortFieldArrayButtonsGroup } from './SortFieldArrayButtonsGroup'

type Props = {
  fieldsSelectOptions: ObjectFieldDTO[] | undefined
  isLoading: boolean
  isDisabled: boolean
  watchedObject: any
}

export const SortFieldArray = ({
  fieldsSelectOptions,
  isLoading,
  isDisabled = false,
  watchedObject,
}: Props) => {
  const { control, trigger, watch } = useFormContext()
  const { fields, append, remove } = useFieldArray({ name: 'sort.value', control })
  const { t } = useTranslation()
  const watchDefaultSortList = watch('sort.value')

  useEffect(() => {
    if (fields.length === 0) {
      append({ field: null, order: null })
    }
  }, [fields])

  const handleAppend = () => {
    append({ field: null, order: null })
  }

  const handleRemove = (index: number) => {
    remove(index)
  }

  const handleClearValue = () => {
    remove()
  }

  const selectedPaths = useMemo(() => {
    return (watchDefaultSortList as { field: ConfigField; order: SORT_ORDER_TYPE }[])
      ?.filter(item => item.field?.pathStr)
      .map(field => field.field.pathStr && field.field.pathStr)
  }, [watchDefaultSortList])

  const getOrderSelectOptionDisabled = useCallback(
    (option: SelectOption, index: number) => {
      return option.id === watch(`sort.value.${index}.order`)
    },
    [watchDefaultSortList]
  )

  const getCurrentFieldValue = useCallback((index: number) => {
    const watchFieldValue = watch(`sort.value.${index}.field`)

    return structuredClone(watchFieldValue)
  }, [])

  return (
    <>
      <Box display={'flex'} justifyContent={'space-between'} mt={2}>
        <Typography variant='h5'>{t('ganttCreate.mainObjectForm.defaultSort')}</Typography>
        <SortFieldArrayButtonsGroup onAppend={handleAppend} onClearAll={handleClearValue} />
      </Box>
      {fields.map((field, index) => (
        <Box key={field.id} alignItems='center' display='flex' mt={2}>
          <Box key={field.id} flex={0.4}>
            <ModalRefPathPicker
              hasClearInInput
              hasField
              currentValue={getCurrentFieldValue(index)}
              embeddedObjectPickerControllerName={`sort.value.${index}.field.pathArray`}
              fieldName={`sort.value.${index}.field.field`}
              isDisabled={isDisabled}
              label={t('ganttCreate.mainObjectForm.field')}
              name={`sort.value.${index}.field.pathStr`}
              object={watchedObject}
              pickerName={`sort.value.${index}.field`}
              selectedPaths={selectedPaths}
              onSave={() => trigger(`sort.value.${index}.field`)}
            />
          </Box>
          <Divider flexItem orientation='vertical' sx={{ width: '10px', mx: 5 }} />
          <Box key={field.id} flex={0.3}>
            <FormInput
              disabled={isDisabled}
              getSelectOptionDisabled={option => getOrderSelectOptionDisabled(option, index)}
              inputType={GENERATOR_INPUT_TYPE.SELECT}
              label={t('ganttCreate.mainObjectForm.order')}
              name={`sort.value.${index}.order`}
              selectOptions={getBindTypeOptionsByEnum(SORT_ORDER_TYPE)}
              variant='outlined'
              onChangeSelect={() => trigger(`sort.value.${index}.order`)}
            />
          </Box>
          <Box flex={0.1} ml={1}>
            <IconButton onClick={() => handleRemove(index)}>
              <Delete fontSize='small' />
            </IconButton>
          </Box>
        </Box>
      ))}
      {fields.length >= 5 && (
        <Box display={'flex'} justifyContent={'end'} mt={2}>
          <SortFieldArrayButtonsGroup onAppend={handleAppend} onClearAll={handleClearValue} />
        </Box>
      )}
    </>
  )
}
